/* eslint-disable */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DatePicker, SelectPicker } from 'rsuite';
import { Loader2, ChevronDown, ChevronUp, Search, AlertCircle } from 'lucide-react';
import moment from 'moment';
import { AutoSizer, List, CellMeasurer, CellMeasurerCache } from 'react-virtualized';
import { BATCH_SOURCE_TABS } from './batchSource';

const BatchPerformanceModalContent = ({
    onClose,
    batches: initialBatches,
    initialFilters,
    onApply,
    selectedBatch,
    setSelectedBatch,
    onSelectProductBatches,
    onViewDetails,
    shiftId: defaultShiftId,
    onShiftChange,
    customerId,
    isSingleSelect = false,
    isSingleDate = false,
    // When set (e.g. 3 from the AI FMS page), restricts the user to picking a
    // date range no longer than this many days and shows a hint.  Leaving it
    // undefined keeps the original "no cap" behaviour for other callers.
    maxDateRangeDays = null,
    // Shows a master "Select All" checkbox that selects every batch currently
    // in view (respects search + shift filters). Enabled only by callers that
    // opt in (e.g. the operation summary page); ignored in single-select mode.
    activeSelectAllOption = false,
    // Planned / Adhoc / All. The parent owns the state and hands down an already
    // filtered `batches`, so everything downstream here needs no source awareness.
    batchSource,
    onBatchSourceChange,
}) => {
    const productList = useSelector((state) => state?.form?.ALL_PRODUCTS?.data?.LIST);
    const cache = useRef(new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 80,
        minHeight: 50,
    }));
    const [batchList, setBatchList] = useState(initialBatches || []);
    const [selectedShiftId, setSelectedShiftId] = useState(defaultShiftId === -999 ? null : defaultShiftId);
    const [isLoading, setIsLoading] = useState(false);
    const [startDate, setStartDate] = useState((initialFilters?.dateRange?.from || initialFilters?.from) ? moment(initialFilters?.dateRange?.from || initialFilters?.from).toDate() : null);
    const [endDate, setEndDate] = useState((initialFilters?.dateRange?.to || initialFilters?.to) ? moment(initialFilters?.dateRange?.to || initialFilters?.to).toDate() : null);
    const [expandedProducts, setExpandedProducts] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const listRef = useRef(null);

    const hasDateChanged = useMemo(() => {
        const initialFrom = initialFilters?.dateRange?.from || initialFilters?.from;
        const initialTo = initialFilters?.dateRange?.to || initialFilters?.to;

        if (!startDate || (initialFrom && !moment(startDate).isSame(moment(initialFrom), 'day'))) return true;
        if (!isSingleDate && (!endDate || (initialTo && !moment(endDate).isSame(moment(initialTo), 'day')))) return true;

        return false;
    }, [startDate, endDate, initialFilters, isSingleDate]);

    // Optional cap on the selectable date range. Only enforced when the
    // caller passes maxDateRangeDays (e.g. AI FMS = 3); otherwise behaves
    // exactly as before with no restriction.
    const hasDateLimit = typeof maxDateRangeDays === 'number' && maxDateRangeDays > 0;
    const MAX_RANGE_DAYS = hasDateLimit ? maxDateRangeDays : null;

    // When user changes the start date and the existing end date now falls
    // outside the allowed window, snap the end date to the closest valid value.
    const handleStartDateChange = (val) => {
        setStartDate(val);
        if (!hasDateLimit || isSingleDate || !val) return;
        if (!endDate) return;
        const diff = moment(endDate).startOf('day').diff(moment(val).startOf('day'), 'days');
        if (diff < 0) {
            // End is before new start — snap to start.
            setEndDate(val);
        } else if (diff > MAX_RANGE_DAYS - 1) {
            // End is more than the cap after start — clamp.
            setEndDate(moment(val).add(MAX_RANGE_DAYS - 1, 'days').toDate());
        }
    };

    // Greys out end-date picker days that fall outside the allowed window.
    const disableEndDate = (date) => {
        if (!hasDateLimit || !startDate) return false;
        const diff = moment(date).startOf('day').diff(moment(startDate).startOf('day'), 'days');
        return diff < 0 || diff > MAX_RANGE_DAYS - 1;
    };

    useEffect(() => {
        setBatchList(initialBatches || []);
        setIsLoading(false);
    }, [initialBatches]);

    // Auto-select batches if a shift is pre-selected on mount
    useEffect(() => {
        if (selectedShiftId && batchList.length > 0 && onSelectProductBatches && selectedBatch.length === 0) {
            const shiftBatches = batchList.filter(b => b.shiftId == selectedShiftId);
            if (shiftBatches.length > 0) {
                onSelectProductBatches(shiftBatches, false);
            }
        }
    }, [batchList, selectedShiftId, onSelectProductBatches, selectedBatch.length]);

    const handleApply = () => {
        if (!startDate) return;
        let actualEndDate = isSingleDate ? startDate : endDate;
        if (!actualEndDate) return;

        // Defensive clamp: never let the request go beyond the configured
        // window when a date limit is in effect.
        if (hasDateLimit && !isSingleDate) {
            const diff = moment(actualEndDate).startOf('day').diff(moment(startDate).startOf('day'), 'days');
            if (diff < 0) {
                actualEndDate = startDate;
            } else if (diff > MAX_RANGE_DAYS - 1) {
                actualEndDate = moment(startDate).add(MAX_RANGE_DAYS - 1, 'days').toDate();
            }
            if (actualEndDate !== endDate) setEndDate(actualEndDate);
        }

        setIsLoading(true);
        if (onApply) {
            onApply(startDate, actualEndDate);
        }
    };

    const metadataMap = useMemo(() => {
        const map = new Map();
        if (!productList) return map;

        productList.forEach(product => {
            product.variants?.forEach(variant => {
                const key = `${product.id}_${variant.variantId}`;
                map.set(key, {
                    productName: product.productName,
                    variantName: variant.variantName || product.variantName || '',
                    displayImage: product.productImageUrl,
                    skuCode: variant.clientVariantId,
                });
            });
            if (!map.has(product.id)) {
                map.set(product.id, {
                    productName: product.productName,
                    variantName: product.variantName || '',
                    displayImage: product.productImageUrl,
                    skuCode: '',
                });
            }
        });
        return map;
    }, [productList]);

    const availableShifts = useMemo(() => {
        if (!initialBatches) return [];
        const uniqueShifts = new Map();
        initialBatches.forEach(b => {
            if (b.shiftId && b.shiftName) {
                uniqueShifts.set(b.shiftId, b.shiftName);
            }
        });
        return Array.from(uniqueShifts.entries()).map(([id, name]) => ({
            label: name,
            value: id
        }));
    }, [initialBatches]);

    const filteredBatches = useMemo(() => {
        if (!selectedShiftId) return batchList;
        return batchList.filter(b => b.shiftId == selectedShiftId);
    }, [batchList, selectedShiftId]);

    const groupedProducts = useMemo(() => {
        if (!filteredBatches) return [];
        const query = searchQuery.toLowerCase();
        const grouped = {};

        filteredBatches.forEach(batch => {
            const metadataKey = `${batch.productId}_${batch.variantId}`;
            const metadata = metadataMap.get(metadataKey) || metadataMap.get(batch.productId) || {
                productName: batch.productName || 'Unknown Product',
                variantName: batch.variantName || '',
                displayImage: '',
                skuCode: '',
            };

            const isMatch = !query ||
                metadata.productName?.toLowerCase().includes(query) ||
                batch.batchName?.toLowerCase().includes(query) ||
                batch.grnItemBatchId?.toString().includes(query) ||
                metadata.skuCode?.toLowerCase().includes(query);

            if (isMatch) {
                if (!grouped[metadataKey]) {
                    grouped[metadataKey] = {
                        ...metadata,
                        batches: []
                    };
                }
                const processedBatch = {
                    ...batch,
                    grnItemBatchId: batch.grnItemBatchId || `D-${batch.productId}-${batch.variantId}-${batch.shiftId}-${batch.costingDate}`
                };
                grouped[metadataKey].batches.push(processedBatch);
            }
        });

        return Object.values(grouped);
    }, [filteredBatches, metadataMap, searchQuery]);

    const handleShiftChange = useCallback((val) => {
        setSelectedShiftId(val);
        // Notify parent so the choice can be remembered across modal opens.
        if (onShiftChange) onShiftChange(val);
        if (val && onSelectProductBatches) {
            const shiftBatches = batchList.filter(b => b.shiftId == val);
            onSelectProductBatches(shiftBatches, false);
        } else if (!val && onSelectProductBatches) {
            // Clear all batches when shift selection is removed
            onSelectProductBatches(selectedBatch, true);
        }
    }, [batchList, onSelectProductBatches, selectedBatch, onShiftChange]);

    useEffect(() => {
        cache.current.clearAll();
        listRef.current?.recomputeRowHeights();
    }, [groupedProducts?.length, expandedProducts?.size, searchQuery]);

    const toggleExpand = (productName) => {
        setExpandedProducts((prev) => {
            const next = new Set(prev);
            if (next.has(productName)) {
                next.delete(productName);
            } else {
                next.add(productName);
            }
            return next;
        });
    };

    const rowRenderer = ({ index, isScrolling, isVisible, key, parent, style }) => {
        const product = groupedProducts[index];
        if (!product) return null;

        const isExpanded = expandedProducts.has(product.productName);
        const visibleBatches = isExpanded ? product.batches : product.batches.slice(0, 5);
        const hasMore = product.batches.length > 5;

        return (
            <CellMeasurer
                cache={cache.current}
                columnIndex={0}
                key={key}
                parent={parent}
                rowIndex={index}
            >
                <div style={style} className='py-1'>
                    <div className='flex gap-4 group items-start p-2 hover:bg-gray-50 rounded-xl transition-colors border border-transparent hover:border-gray-100 bg-white'>
                        {/* Image */}
                        <div className='w-[75px] h-[75px] rounded-lg overflow-hidden border border-gray-100 flex-shrink-0 shadow-sm bg-gray-50'>
                            <img src={product.displayImage} alt={product.productName} className='w-full h-full object-cover' />
                        </div>

                        {/* Details */}
                        <div className='flex-1 flex flex-col gap-2.5 justify-start'>
                            <h4 className='text-[14px] font-bold text-[#1F2937] leading-tight text-left flex items-center gap-2'>
                                {!isSingleSelect && onSelectProductBatches && (() => {
                                    const allSelected = product.batches.every((b) =>
                                        selectedBatch?.find((sb) => sb.grnItemBatchId === b.grnItemBatchId)
                                    );
                                    const someSelected = !allSelected && product.batches.some((b) =>
                                        selectedBatch?.find((sb) => sb.grnItemBatchId === b.grnItemBatchId)
                                    );
                                    return (
                                        <input
                                            type='checkbox'
                                            checked={allSelected}
                                            ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                onSelectProductBatches(product.batches, allSelected);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            className='w-3.5 h-3.5 accent-[#4338CA] cursor-pointer flex-shrink-0'
                                            title={allSelected ? 'Deselect all batches' : 'Select all batches'}
                                        />
                                    );
                                })()}
                                {product.productName} {product.variantName ? ` (${product.variantName})` : ''}
                                {product.skuCode && (
                                    <span className='px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded font-medium border border-gray-200 uppercase tracking-tight'>
                                        {product.skuCode}
                                    </span>
                                )}
                            </h4>

                            <div className='flex flex-wrap gap-1.5'>
                                {visibleBatches.map((batch) => (
                                    <div
                                        key={batch.grnItemBatchId}
                                        onClick={customerId === 72 || isSingleSelect ? () => setSelectedBatch(batch) : undefined}
                                        className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-all select-none
                                            ${customerId === 72 || isSingleSelect ? 'cursor-pointer' : 'cursor-default'}
                                            ${selectedBatch?.find((b) => b?.grnItemBatchId === batch?.grnItemBatchId)
                                                ? 'bg-[#4338CA] text-white border-[#4338CA] shadow-md scale-105'
                                                : 'bg-[#EEF2FF] text-[#4338CA] border-[#C7D2FE] hover:bg-[#E0E7FF]'
                                            }`}
                                    >
                                        <div className='flex flex-col items-start gap-0'>
                                            <span>{batch.batchName}</span>
                                            <span className={`text-[9px] font-medium ${selectedBatch?.find((b) => b?.grnItemBatchId === batch?.grnItemBatchId) ? 'text-indigo-100' : 'text-gray-400'}`}>
                                                {batch.costingDate || 'N/A'} {batch.shiftName ? `(${batch.shiftName})` : ''}
                                            </span>
                                        </div>
                                    </div>
                                ))}

                                {hasMore && (
                                    <button
                                        onClick={() => toggleExpand(product.productName)}
                                        className='flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold text-gray-500 hover:text-[#4338CA] hover:bg-white border border-transparent hover:border-gray-200 transition-all'
                                    >
                                        {isExpanded ? (
                                            <><ChevronUp size={12} /> Less</>
                                        ) : (
                                            <><ChevronDown size={12} /> {product.batches.length - 5} More</>
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </CellMeasurer>
        );
    };

    return (
        <div className='flex flex-col gap-4 w-full h-full bg-white rounded-lg overflow-hidden'>
            {/* Filter Section */}
            <div className='flex items-center gap-3 px-6 mt-4'>
                <style>
                    {`
                    @keyframes pulse-yellow {
                        0% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.7); }
                        70% { box-shadow: 0 0 0 10px rgba(251, 191, 36, 0); }
                        100% { box-shadow: 0 0 0 0 rgba(251, 191, 36, 0); }
                    }
                    .animate-pulse-yellow {
                        animation: pulse-yellow 2s infinite;
                    }
                    `}
                </style>
                <div className='flex-1'>
                    <DatePicker
                        placeholder={isSingleDate ? 'Select Date (YYYY-MM-DD)' : 'Start date (YYYY-MM-DD)'}
                        style={{ width: '100%', borderRadius: '8px' }}
                        size='sm'
                        value={startDate}
                        onChange={handleStartDateChange}
                        format='yyyy-MM-dd'
                        oneTap
                    />
                </div>
                {!isSingleDate && (
                    <div className='flex-1'>
                        <DatePicker
                            placeholder='End date (YYYY-MM-DD)'
                            style={{ width: '100%', borderRadius: '8px' }}
                            size='sm'
                            value={endDate}
                            onChange={setEndDate}
                            format='yyyy-MM-dd'
                            oneTap
                            shouldDisableDate={disableEndDate}
                        />
                    </div>
                )}
                <button
                    onClick={handleApply}
                    disabled={isLoading}
                    className={`bg-[#fbbf24] hover:bg-[#f59e0b] text-black font-bold px-6 py-1.5 rounded-lg transition-all active:scale-95 shadow-sm disabled:opacity-50 flex items-center gap-2 text-sm ${hasDateChanged ? 'animate-pulse-yellow' : ''}`}
                >
                    {isLoading && <Loader2 size={14} className='animate-spin' />}
                    Apply
                </button>
            </div>

            {/* Date-range limit hint (only when a cap is configured) */}
            {hasDateLimit && !isSingleDate && (
                <div className='px-6 -mt-2 flex items-center gap-1.5 text-[11px] text-gray-500'>
                    <AlertCircle size={12} className='text-gray-400' />
                    <span>You can pick a date range of up to {MAX_RANGE_DAYS} days at a time.</span>
                </div>
            )}

            {/* Change Warning Notice */}
            {hasDateChanged && (
                <div className='px-6'>
                    <div className='bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5 rounded-md flex items-center gap-2 text-[12px] font-semibold animate-in fade-in slide-in-from-top-1 duration-300'>
                        <AlertCircle size={14} className='text-amber-600' />
                        Selected date range has changed. Click Apply to refresh the batch list.
                    </div>
                </div>
            )}

            {/* Search and Shift Filter Section */}
            <div className='px-6 -mt-1 flex items-center gap-3'>
                <div className='relative group flex-1'>
                    <span className='absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 group-focus-within:text-[#4338CA] transition-colors'>
                        <Search size={16} />
                    </span>
                    <input
                        type='text'
                        placeholder='Search by product name, batch name or ID...'
                        className='w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4338CA]/20 focus:border-[#4338CA] transition-all'
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            onClick={() => setSearchQuery('')}
                            className='absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600'
                        >
                            <span className='text-xs font-bold bg-gray-200 hover:bg-gray-300 w-5 h-5 flex items-center justify-center rounded-full'>×</span>
                        </button>
                    )}
                </div>

                {onBatchSourceChange && !isSingleSelect && (
                    <div className='flex w-fit rounded-lg border border-indigo-100 bg-indigo-50 overflow-hidden'>
                        {BATCH_SOURCE_TABS.map((ele) => {
                            const isActive = batchSource === ele.id;
                            return (
                                <button
                                    key={ele.id}
                                    type='button'
                                    onClick={() => onBatchSourceChange(ele.id)}
                                    className={`px-2.5 py-2 text-[12px] font-bold whitespace-nowrap transition-colors
                                        ${isActive ? 'bg-[#4338CA] text-white' : 'text-[#4338CA] hover:bg-indigo-100'}`}
                                >
                                    {ele.label}
                                </button>
                            );
                        })}
                    </div>
                )}

                {activeSelectAllOption && !isSingleSelect && onSelectProductBatches && (() => {
                    const allBatchesInView = groupedProducts.flatMap((p) => p.batches);
                    const allSelected = allBatchesInView.length > 0 && allBatchesInView.every((b) =>
                        selectedBatch?.find((sb) => sb.grnItemBatchId === b.grnItemBatchId)
                    );
                    const someSelected = !allSelected && allBatchesInView.some((b) =>
                        selectedBatch?.find((sb) => sb.grnItemBatchId === b.grnItemBatchId)
                    );
                    return (
                        <label className={`flex items-center gap-1.5 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg select-none whitespace-nowrap ${allBatchesInView.length === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-indigo-100'} transition-colors`}>
                            <input
                                type='checkbox'
                                checked={allSelected}
                                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                onChange={() => onSelectProductBatches(allBatchesInView, allSelected)}
                                disabled={allBatchesInView.length === 0}
                                className='w-3.5 h-3.5 accent-[#4338CA] cursor-pointer'
                            />
                            <span className='text-[12px] font-bold text-[#4338CA]'>
                                Select All ({allBatchesInView.length})
                            </span>
                        </label>
                    );
                })()}

                {customerId !== 72 && !isSingleSelect && (
                    <div className='flex items-center gap-2'>
                        <SelectPicker
                            data={availableShifts}
                            value={selectedShiftId}
                            onChange={handleShiftChange}
                            placeholder="Filter by Shift"
                            onClean={() => {
                                handleShiftChange(null);
                            }}
                            menuStyle={{
                                zIndex: 99999,
                            }}
                            style={{ width: 180 }}
                            size="md"
                            cleanable
                        />
                    </div>
                )}

            </div>

            {/* Listing Section slice */}
            <div className={`h-[calc(100vh-420px)] px-6 flex flex-col gap-4 custom-scrollbar mb-4 relative transition-opacity duration-300 ${hasDateChanged ? 'opacity-60 select-none' : 'opacity-100'}`}>
                {hasDateChanged && !isLoading && (
                    <div className='absolute inset-0 z-20 flex items-center justify-center bg-gray-50/20 backdrop-blur-[1px] cursor-not-allowed'>
                        <div className='bg-white/90 border border-amber-200 shadow-lg px-4 py-2 rounded-xl text-amber-800 text-sm font-bold flex items-center gap-2'>
                            <AlertCircle size={18} className='text-amber-500 animate-pulse' />
                            Refresh required for new date selection
                        </div>
                    </div>
                )}
                {isLoading && (
                    <div className='absolute inset-0 bg-white/50 z-10 flex items-center justify-center backdrop-blur-[1px]'>
                        <Loader2 size={28} className='animate-spin text-[#4338CA]' />
                    </div>
                )}

                {groupedProducts.length === 0 && !isLoading ? (
                    <div className='flex flex-col items-center justify-center h-full text-gray-400 py-8'>
                        <p className='text-sm'>No batches found for the selected range.</p>
                    </div>
                ) : (
                    <div className='flex-1'>
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    ref={listRef}
                                    height={height}
                                    width={width}
                                    rowCount={groupedProducts.length}
                                    rowHeight={cache.current.rowHeight}
                                    deferredMeasurementCache={cache.current}
                                    rowRenderer={rowRenderer}
                                    className='custom-scrollbar'
                                />
                            )}
                        </AutoSizer>
                    </div>
                )}
            </div>

            {/* Sticky Footer */}
            <div className='border-t border-gray-100 p-2 flex items-center justify-end bg-gray-50/50'>
                <div className='flex gap-3'>
                    <button
                        onClick={onClose}
                        className='px-5 py-2 text-[13px] font-bold text-[#4B5563] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors'
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onViewDetails}
                        disabled={!selectedBatch || selectedBatch.length === 0}
                        className='px-6 py-2 text-[13px] font-bold text-black bg-[#fbbf24] rounded-lg hover:bg-[#f59e0b] shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                        View Batch Details
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BatchPerformanceModalContent;

